import { inject, Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { HttpParams } from '@angular/common/http';
import { GetCenterLastSnapshotParams, GetCenterLastSnapshotTransformerResponse, } from '../models/snapshots.models';
import {
  GET_CENTER_DETAILS_ENDPOINT,
  GET_INFRAESTRUCTURE_ENDPOINT,
  GET_LAST_SNAPSHOT_ENDPOINT,
} from '../constants/endpoints.constants';
import {
  GetCenterDetailsParams,
  GetCenterDetailsResponse,
  GetCenterInfraestructureParams,
  GetCenterInfraestructureResponse,
} from '../models/common.models';
import { Observable } from 'rxjs';
import { withSpinner } from '../../app/core/services/spinner.controller';

/**
 * DataCentersService provides methods for interacting with data centers' infrastructure
 * and snapshot-related data through the API.
 */
@Injectable({ providedIn: 'root' })
export class DataCentersService {
  protected readonly apiService = inject(ApiService);

  /**
   * Retrieves infrastructure details for a specified data center.
   *
   * @param params - An object of type `GetCenterInfraestructureParams` containing parameters
   * for querying the infrastructure data.
   * @returns An `Observable` of `GetCenterInfraestructureResponse` with the infrastructure details.
   */
  public getCenterInfraestructure(
    params: GetCenterInfraestructureParams,
  ): Observable<GetCenterInfraestructureResponse> {
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetCenterInfraestructureResponse>(
        GET_INFRAESTRUCTURE_ENDPOINT,
        queryParams,
      )
      .pipe(withSpinner());
  }

  /**
   * Fetches the last snapshot data for a specified data center.
   *
   * @param params - An object of type `GetCenterLastSnapshotParams` containing parameters
   * for querying the last snapshot data.
   * @returns An `Observable` of `GetCenterLastSnapshotTransformerResponse` with the snapshot details.
   */
  public getCenterLastSnapshot(
    params: GetCenterLastSnapshotParams,
  ): Observable<GetCenterLastSnapshotTransformerResponse> {
    const queryParams = new HttpParams().appendAll({ ...params });
    return this.apiService
      .get<GetCenterLastSnapshotTransformerResponse>(
        GET_LAST_SNAPSHOT_ENDPOINT,
        queryParams,
      )
      .pipe(withSpinner());
  }

  /**
   * Retrieves detailed information for a specified data center.
   *
   * @param params - An object of type `GetCenterDetailsParams` containing parameters
   * for querying the center details (centerId and distributorId).
   * @returns An `Observable` of `GetCenterDetailsResponse` with the center details including
   * id, name, and updatedAt timestamp.
   */
  public getCenterDetails(
    params: GetCenterDetailsParams,
  ): Observable<GetCenterDetailsResponse> {
    const queryParams = new HttpParams().appendAll({
      distributorId: params.distributorId,
    });
    const endpoint = GET_CENTER_DETAILS_ENDPOINT.replace(
      '{{centerId}}',
      params.centerId,
    );
    return this.apiService
      .get<GetCenterDetailsResponse>(endpoint, queryParams)
      .pipe(withSpinner());
  }
}
